import { cloneElement, isValidElement, type ReactElement, type ReactNode } from 'react';
import { TABLE_PANEL_HEADER_HEIGHT } from '@/features/shared/components/tableSystem/tableConstants';
import { buildTableDividerPseudoSx } from '@/features/shared/components/tableSystem/tableDividerSx';
import { tablePanelSurfaceSx } from '@/features/shared/components/tableSystem/tablePanelSurfaceSx';
import {
  tablePanelTabSx,
  tablePanelTabsSx,
} from '@/features/shared/components/tableSystem/tablePanelTabs';
import { TableToolbar } from '@/features/shared/components/tableSystem/TableToolbar';
import { s } from '@/theme/tokens';
import { Panel } from '@/ui/composites/Panel';
import { Box, Tab, Tabs } from '@/ui/primitives';

export type CoverageDetailTablesPanelTab = {
  value: string;
  label: string;
  toolbar: ReactNode;
  content: ReactNode;
};

type CoverageDetailTablesPanelProps = {
  activeTab: string;
  onTabChange: (nextTab: string) => void;
  tabs: CoverageDetailTablesPanelTab[];
  tabsAriaLabel: string;
};

type DetailToolbarElement = ReactElement<{
  hideDateRangeLabel?: boolean;
  showControlsWithoutSummary?: boolean;
}>;

const isTableToolbarElement = (slot: ReactNode): slot is DetailToolbarElement =>
  isValidElement(slot) && slot.type === TableToolbar;

const buildDetailToolbar = (toolbar: ReactNode): ReactNode =>
  isTableToolbarElement(toolbar)
    ? cloneElement(toolbar, { hideDateRangeLabel: true, showControlsWithoutSummary: true })
    : toolbar;

export const CoverageDetailTablesPanel = ({
  activeTab,
  onTabChange,
  tabs,
  tabsAriaLabel,
}: CoverageDetailTablesPanelProps): ReactElement => {
  const activeTabConfig = tabs.find((tab) => tab.value === activeTab) ?? tabs[0];

  return (
    <Panel
      padding="md"
      gap="none"
      contentGap="md"
      headerGap="none"
      sx={tablePanelSurfaceSx}
      headerSlot={
        <Box
          sx={{
            borderBottom: 'none',
            minHeight: `${TABLE_PANEL_HEADER_HEIGHT}px`,
            position: 'relative',
            '&::after': buildTableDividerPseudoSx('bottom'),
          }}
        >
          <Box
            sx={{
              alignItems: 'flex-end',
              display: 'flex',
              gap: s('sm'),
              justifyContent: 'space-between',
              minHeight: `${TABLE_PANEL_HEADER_HEIGHT}px`,
            }}
          >
            <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
              <Tabs
                value={activeTabConfig.value}
                onChange={(_event, nextValue) => {
                  onTabChange(String(nextValue));
                }}
                aria-label={tabsAriaLabel}
                sx={tablePanelTabsSx}
              >
                {tabs.map((tab) => (
                  <Tab key={tab.value} label={tab.label} value={tab.value} sx={tablePanelTabSx} />
                ))}
              </Tabs>
            </Box>
            <Box sx={{ flex: '0 0 auto' }}>{buildDetailToolbar(activeTabConfig.toolbar)}</Box>
          </Box>
        </Box>
      }
    >
      <Box
        key={activeTabConfig.value}
        role="tabpanel"
        aria-hidden={false}
        sx={{ display: 'block' }}
      >
        {activeTabConfig.content}
      </Box>
    </Panel>
  );
};
